import { HttpRequestError } from "client/Api/httpClient";
import {
	Dialog,
	FormattedText,
	MultiComboBoxItem,
	renderReactDialog,
	Toolbar,
	useAlertDialog
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useForceRerender,
	useStableImperativeCallback,
	useUnmountedCancellation,
	useUserTimezone
} from "client/Hooks";
import {
	ensureNotNil,
	isNotNil,
	isNotNilOrWhiteSpace
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation
} from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined,
	isValidDate
} from "client/Validation/rules";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import {
	DocumentItemViewModel,
	ListLocationViewModel,
	ListLocationModel,
	RecentDocumentItemViewModel
} from "client/Api/Documents/SharePoint/common";
import {
	AnonymousLinkInformation,
	DefaultLinkKind,
	enableAnonymousLink,
	EnableAnonymousLinkModel,
	enableOrganisationLink,
	EnableOrganisationLinkModel,
	enableSpecificPeopleLink,
	EnableSpecificPeopleLinkModel,
	getShareFileInfo,
	SharedLinksInformation,
	SharePointPeoplePickerItemModel
} from "client/Api/Documents/SharePoint/sharePointSharing";
import { EmailClient, getEffectiveEmailClient } from "client_legacy/Email/emailClient";
import { sendLink } from "client_legacy/Email/SendFile/sendFile";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { AdvancedLinkConfigurationSection } from "./AdvancedLinkConfigurationSection";
import {
	AnonymousLinkUpdateResult,
	useAnonymousLinkUpdateConfirmationDialog
} from "./AnonymousLinkUpdateConfirmationDialog";
import { useCopyLinkDialog } from "./CopyLinkDialog";
import { DefaultLinkConfigurationSection } from "./DefaultLinkConfigurationSection";
import { LinkAudienceKind, LinkPermission } from "./common";
import { useManageLinksDialog } from "./ManageLinksDialog";
import { TimeZone } from "client/DateAndTime/timezones";

type ShareALinkDialogProps = {
	document: RecentDocumentItemViewModel | DocumentItemViewModel;
	onClose: () => void;
};

const ShareALinkDialog = (props: ShareALinkDialogProps): ReactElement => {
	const {
		document,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(true);
	const [currentConfiguration, setCurrentConfiguration] = useState<SharedLinksInformation | undefined>();
	const [mode, setMode] = useState<ShareALinkMode>(ShareALinkMode.Default);
	const [formData, setFormData] = useState<FormData>({
		audience: LinkAudienceKind.Anonymous,
		data: {
			allowEditing: false,
			expirationDate: {
				isSet: false,
				value: null
			},
			selectedPeople: []
		}
	});

	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [reload, loadVersion] = useForceRerender();
	const [manageLinksDialog, openManageLinksDialog] = useManageLinksDialog();
	const [copyLinkDialog, openCopyLinkDialog] = useCopyLinkDialog();
	const [anonymousLinkUpdateConfirmationDialog, openAnonymousLinkUpdateConfirmationDialog] = useAnonymousLinkUpdateConfirmationDialog();
	const [alertDialog, showAlertDialog] = useAlertDialog();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const stableOnClose: typeof onClose = useStableImperativeCallback(onClose);

	useEffect(
		(): (() => void) => {
			hasImplicitDependencyOn(loadVersion);

			const load = async (
				location: ListLocationViewModel,
				id: number,
				signal: AbortSignal
			): Promise<void> => {
				try {
					setIsBusy(true);

					const shareInfo: SharedLinksInformation = await getShareFileInfo(
						convertToLocationModel(location),
						id,
						signal
					);

					if (shareInfo.isCompletelyUnavailable) {
						await showAlertDialog({
							title: (
								<FormattedText
									text={localize("_ShareLinkForName")}
									thingsToReplace={[
										{ what: "{{Name}}", filler: document.name }
									]}
								/>
							),
							confirmText: localize("_Ok"),
							trackingId: "sp-share-link-unavailable-dialog",
							children: localize("_DueToSettingsByYourAdminSharingIsCompletelyDisabledPleaseContactYourAdminIfYouWantToHaveSharingEnabled")
						});

						stableOnClose();
						return;
					}

					const [audience, allowEditing] = getAudienceAndPermissionForLinkKind(shareInfo.defaultLinkKind);

					setCurrentConfiguration(shareInfo);

					setFormData({
						audience,
						data: {
							allowEditing,
							expirationDate: {
								isSet: false,
								value: null
							},
							selectedPeople: [],
						}
					});

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
					stableOnClose();
				}
			};

			const cancellation = new AbortController();

			load(
				document.location,
				document.id,
				cancellation.signal
			);

			return () => cancellation.abort();
		},
		[
			alertManager,
			document.id,
			document.location,
			document.name,
			loadVersion,
			stableOnClose,
			showAlertDialog
		]
	);

	const doEnableAnonymousLink = useCallback(
		async (
			id: number,
			location: ListLocationModel,
			data: FormDataData
		): Promise<TryResult<GetOrCreateLinkData>> => {
			const previousConfiguration: SharedLinksInformation = ensureNotNil(currentConfiguration);

			const {
				allowEditing,
				expirationDate
			} = data;

			const existingLinkConfiguration: AnonymousLinkInformation = allowEditing
				? previousConfiguration.anonymousEdit
				: previousConfiguration.anonymousView;

			if (existingLinkConfiguration.isEnabled) {
				if (expirationDate.isSet
					|| isNotNil(existingLinkConfiguration.expirationDate)
				) {
					const confirmationResult: TryResult<AnonymousLinkUpdateResult> = await openAnonymousLinkUpdateConfirmationDialog({
						expirationDate: existingLinkConfiguration.expirationDate
					});

					if (!confirmationResult.wasSuccessful) {
						return confirmationResult;
					}

					switch (confirmationResult.result) {
						case AnonymousLinkUpdateResult.UseExistingSettings:
							return {
								wasSuccessful: true,
								result: {
									audience: LinkAudienceKind.Anonymous,
									permission: allowEditing
										? LinkPermission.CanEdit
										: LinkPermission.CanView,
									url: existingLinkConfiguration.url,
									expirationDate: existingLinkConfiguration.expirationDate
								}
							};
						case AnonymousLinkUpdateResult.UseNewSettings:
							// No early return proceed to actual implementation
							break;
						default:
							never(confirmationResult.result);
					}
				}
			}

			const model: EnableAnonymousLinkModel = {
				location,
				itemId: id,
				data: {
					allowEditing,
					expirationDate: expirationDate.isSet
						? {
							isSet: true,
							value: ensureNotNil(expirationDate.value)
								.toISOString()
						}
						: {
							isSet: false
						}
				}
			};

			const url: string = await enableAnonymousLink(
				model,
				globalCancellation
			);

			return {
				wasSuccessful: true,
				result: {
					audience: LinkAudienceKind.Anonymous,
					permission: model.data.allowEditing
						? LinkPermission.CanEdit
						: LinkPermission.CanView,
					url: url,
					expirationDate: model.data
						.expirationDate
						.isSet
							? model.data.expirationDate.value
							: undefined
				}
			};
		},
		[
			currentConfiguration,
			globalCancellation,
			openAnonymousLinkUpdateConfirmationDialog
		]
	);

	const doEnableSpecificPeopleLink = useCallback(
		async (
			id: number,
			location: ListLocationModel,
			data: FormDataData
		): Promise<TryResult<GetOrCreateLinkData>> => {
			const { allowEditing, selectedPeople } = data;

			const model: EnableSpecificPeopleLinkModel = {
				location,
				itemId: id,
				data: {
					selectedPeople: selectedPeople.map(x => x.value),
					allowEditing
				}
			};

			const url: string = await enableSpecificPeopleLink(
				model,
				globalCancellation
			);

			return {
				wasSuccessful: true,
				result: {
					audience: LinkAudienceKind.SpecificPeople,
					permission: model.data.allowEditing
						? LinkPermission.CanEdit
						: LinkPermission.CanView,
					url: url
				}
			};
		},
		[globalCancellation]
	);

	const doEnableOrganizationLink = useCallback(
		async (
			id: number,
			location: ListLocationModel,
			data: FormDataData
		): Promise<TryResult<GetOrCreateLinkData>> => {
			const { allowEditing } = data;

			const model: EnableOrganisationLinkModel = {
				location,
				itemId: id,
				data: {
					allowEditing
				}
			};

			const organizationUrl: string = await enableOrganisationLink(
				model,
				globalCancellation
			);

			return {
				wasSuccessful: true,
				result: {
					audience: LinkAudienceKind.Organization,
					permission: model.data.allowEditing
						? LinkPermission.CanEdit
						: LinkPermission.CanView,
					url: organizationUrl
				}
			};
		},
		[globalCancellation]
	);

	const getOrCreateLink = useCallback(
		async (): Promise<TryResult<GetOrCreateLinkData>> => {
			if (isBusy) {
				return {
					wasSuccessful: false
				};
			}

			setIsBusy(true);

			const validation: IRootValidation<FormData> = formHandler.validation;

			const id: number = document.id;
			const location: ListLocationModel = convertToLocationModel(document.location);

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return {
						wasSuccessful: false
					};
				}

				let result: TryResult<GetOrCreateLinkData>;
				switch (formData.audience) {
					case LinkAudienceKind.Anonymous:
						result = await doEnableAnonymousLink(
							id,
							location,
							formData.data
						);
						break;
					case LinkAudienceKind.SpecificPeople:
						result = await doEnableSpecificPeopleLink(
							id,
							location,
							formData.data
						);
						break;
					case LinkAudienceKind.Organization:
						result = await doEnableOrganizationLink(
							id,
							location,
							formData.data
						);
						break;
					case LinkAudienceKind.PeopleWithExistingAccess:
						const url: string | nil = currentConfiguration?.peopleWithExistingAccessUrl;
						if (isNotNilOrWhiteSpace(url)) {
							result = {
								wasSuccessful: true,
								result: {
									audience: LinkAudienceKind.PeopleWithExistingAccess,
									permission: LinkPermission.CanView,
									url
								}
							};
						} else {
							result = {
								wasSuccessful: false
							};
						}
						break;
					default:
						never(formData.audience);
				}

				setIsBusy(false);
				return result;

			} catch (e) {
				if (isCancelledError(e)) {
					return {
						wasSuccessful: false
					};
				}

				if (!validation.tryProcessServerError(e)) {
					alertManager.showError(localize("_FailedToCreateThisLink"));
				}

				setIsBusy(false);
				return {
					wasSuccessful: false
				};
			}
		},
		[
			alertManager,
			currentConfiguration?.peopleWithExistingAccessUrl,
			document.id,
			document.location,
			formData.audience,
			formData.data,
			formHandler.validation,
			doEnableAnonymousLink,
			doEnableOrganizationLink,
			doEnableSpecificPeopleLink,
			globalCancellation,
			isBusy
		]
	);

	const copyHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<GetOrCreateLinkData> = await getOrCreateLink();
			if (!result.wasSuccessful) {
				return;
			}

			const {
				audience,
				permission,
				url,
				expirationDate
			} = result.result;

			await openCopyLinkDialog({
				documentName: document.name,
				audience: audience,
				permission: permission,
				url,
				expirationDate
			});

			onClose();
		},
		[
			document.name,
			getOrCreateLink,
			onClose,
			openCopyLinkDialog
		]
	);

	const emailClient = useMemo(
		(): EmailClient => getEffectiveEmailClient(),
		[]
	);

	const emailHandler = useMemo(
		(): (() => Promise<void>) | undefined => {
			const isEmailLinkEnabled: boolean = emailClient.supportsEmailComposeWithBody
				|| emailClient.supportsEditDraft;

			if (!isEmailLinkEnabled) {
				return undefined;
			}

			return async (): Promise<void> => {
				const result: TryResult<GetOrCreateLinkData> = await getOrCreateLink();
				if (!result.wasSuccessful) {
					return;
				}

				const {
					url
				} = result.result;

				if (emailClient.supportsEditDraft) {
					setIsBusy(true);

					try {
						await sendLink(
							url,
							emailClient
						);
					} catch (e) {
						setIsBusy(false);

						let message: string | undefined;
						if (e instanceof HttpRequestError) {
							try {
								const errorResponse: IUserFriendlyExceptionMessage = e.readJson<IUserFriendlyExceptionMessage>();
								message = errorResponse.message;
							} catch (parseError) {
								console.error(parseError);
							}
						}

						if (!message) {
							message = localize("_GeneralErrorMessage");
						}

						alertManager.showError(message);
					} finally {
						onClose();
					}
				} else if (emailClient.supportsEmailComposeWithBody) {
					emailClient.composeEmailWithBody({
						body: url
					});

					onClose();
				} else {
					throw new Error("Email app launcher does not support sharing links.");
				}
			};
		},
		[
			alertManager,
			emailClient,
			getOrCreateLink,
			onClose
		]
	);

	const manageLinksHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<{ goBack: boolean; }> = await openManageLinksDialog({
				document
			});

			if (result.wasSuccessful
				&& result.result.goBack
			) {
				reload();
				return;
			}

			stableOnClose();
		},
		[
			document,
			stableOnClose,
			openManageLinksDialog,
			reload
		]
	);

	const moreActions = useMemo(
		(): readonly ShareALinkAction[] => {
			return listMoreActions({
				manage: manageLinksHandler
			});
		},
		[manageLinksHandler]
	);

	if (copyLinkDialog) {
		return copyLinkDialog;
	}

	if (manageLinksDialog) {
		return manageLinksDialog;
	}

	if (alertDialog) {
		return alertDialog;
	}

	const renderContent = (): ReactElement => {
		switch (mode) {
			case ShareALinkMode.Default:
				return (
					<DefaultLinkConfigurationSection
						audience={formData.audience}
						allowEditing={formData.data.allowEditing}
						onChangeSettings={() => setMode(ShareALinkMode.Advanced)}
					/>
				);
			case ShareALinkMode.Advanced:
				return (
					<AdvancedLinkConfigurationSection
						formHandler={formHandler}
						areAnonymousLinksAllowed={currentConfiguration?.areAnonymousLinksAllowed ?? true}
						isShareByEmailEnabled={currentConfiguration?.isShareByEmailEnabled ?? false}
						location={document.location}
						timezone={userTimezone}
					/>
				);
			default:
				never(mode);
		}
	};

	return (
		<>
			<Dialog trackingId="sp-share-link-dialog" hasBusyIndicator={isBusy}>
				<Dialog.Header
					title={(
						<FormattedText
							text={localize("_ShareLinkForName")}
							thingsToReplace={[
								{ what: "{{Name}}", filler: document.name }
							]}
						/>
					)}
					onClose={onClose}
					actionContext={undefined}
					actions={moreActions}
				/>

				<Dialog.Body>
					{renderContent()}
				</Dialog.Body>

				<Dialog.Footer>
					<Toolbar>
						<Toolbar.Button
							kind="primary"
							text={localize("_CopyLink")}
							onClick={copyHandler}
							trackingId="copy"
						/>

						{emailHandler && (
							<Toolbar.Button
								kind="primary"
								text={localize("_EmailLink")}
								onClick={emailHandler}
								trackingId="email"
							/>
						)}

						<Toolbar.Button
							kind="secondary"
							text={localize("_Cancel")}
							onClick={onClose}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>

			{anonymousLinkUpdateConfirmationDialog}
		</>
	);
};
ShareALinkDialog.displayName = "ShareALinkDialog";

export type FormDataData = {
	allowEditing: boolean;
	expirationDate: {
		isSet: boolean;
		value: Date | null;
	};
	selectedPeople: readonly MultiComboBoxItem<SharePointPeoplePickerItemModel>[];
};

export type FormData = {
	audience: LinkAudienceKind;
	data: FormDataData;
};

const validationConfiguration = createValidationConfiguration<FormData>();

validationConfiguration
	.when(x => x.audience === LinkAudienceKind.Anonymous)
	.forObject("data")
		.forObject("expirationDate")
		.when(x => x.isSet)
		.forScalar("value")
			.add(
				isNotNullNorUndefined(),
				isValidDate()
			);

validationConfiguration
	.when(x => x.audience === LinkAudienceKind.SpecificPeople)
	.forObject("data")
		.forCollection("selectedPeople")
			.add(
				isNotEmpty()
			);

enum ShareALinkMode {
	Default = 1,
	Advanced = 2
}

function getAudienceAndPermissionForLinkKind(kind: DefaultLinkKind): [LinkAudienceKind, allowEditing: boolean] {
	switch (kind) {
		case DefaultLinkKind.AnonymousEdit:
			return [LinkAudienceKind.Anonymous, true];
		case DefaultLinkKind.OrganizationEdit:
			return [LinkAudienceKind.Organization, true];
		case DefaultLinkKind.AnonymousReadOnly:
			return [LinkAudienceKind.Anonymous, false];
		case DefaultLinkKind.OrganizationReadOnly:
			return [LinkAudienceKind.Organization, false];
		case DefaultLinkKind.PeopleWithExistingAccess:
			// PeopleWithExistingAccess doesn't have the concept of read/edit, so we default to the safer "read"
			return [LinkAudienceKind.PeopleWithExistingAccess, false];
		default:
			never(kind);
	}
}

type ShareALinkActionContext = undefined;
type ShareALinkAction = Action<ShareALinkActionContext>;

type ShareALinkHandlers = {
	manage: () => void;
};

function listMoreActions(
	handlers: ShareALinkHandlers
): readonly ShareALinkAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "manage-links",
			icon: "gear",
			title: localize("_ManageLinks"),
			canExecute: () => true,
			execute: () => handlers.manage()
		}
	];
}

type GetOrCreateLinkData = {
	audience: LinkAudienceKind;
	permission: LinkPermission;
	url: string;
	expirationDate?: string | Date;
};

export function openShareALinkDialog(props: Omit<ShareALinkDialogProps, "onClose">): void {
	return renderReactDialog(
		(onClose) => (
			<ShareALinkDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
